import { AdmDataGrid } from "@components/common/data-grid/adm-data-grid";
import { useEffect, useState } from "react";
import { useAppDispatch, useAppSelector } from "@store/store-helper";
import { fetchCompanyProjects } from "@store/workspace/workspace-thunks";
import { BaseCompanyIdProps } from "@custom-types/base-types";
import { companyStateSelector } from "@store/workspace/workspace-slice";
import { Spinner } from "@components/common/spinner/spinner";
import { ContentLayout } from "@pages/content-layout";
import { useErrorHandler } from "@components/error-boundary/error-handling-context";
import { useWorkspaceProjectsTableColumns } from "@pages/workspace-profile/tab-contents/workspace-projects-tab/use-workspace-projects-table-columns";
import { useAppNavigation } from "@router/use-app-navigation";
import { CSVDataUtils } from "@utils/csv-data";

const ERROR_MESSAGE = "Could not load projects of the workspace";

/** Fetch workspace projects and display them in a table */
export function WorkspaceProjects({
  companyId,
}: BaseCompanyIdProps): React.JSX.Element {
  const { navigateToProfilePage } = useAppNavigation();
  const { handleErrorWithToast } = useErrorHandler();
  const [shouldShowErrorMessage, setShouldShowErrorMessage] =
    useState<boolean>(false);

  const columns = useWorkspaceProjectsTableColumns();

  const { projects, fetchingCompany } = useAppSelector(companyStateSelector);

  const dispatch = useAppDispatch();

  // Fetches the company projects
  useEffect(() => {
    setShouldShowErrorMessage(false);
    dispatch(fetchCompanyProjects({ companyId }))
      .unwrap()
      .catch((error) => {
        handleErrorWithToast({
          id: `CompanyProjects-${Date.now().toString()}`,
          title: ERROR_MESSAGE,
          error,
        });
        setShouldShowErrorMessage(true);
      });
  }, [companyId, dispatch, handleErrorWithToast]);

  if (fetchingCompany.companyProjects === "pending") {
    return <Spinner />;
  }

  return (
    <ContentLayout contentSx={{ overflowY: "hidden" }}>
      <AdmDataGrid
        entities={projects}
        uniqueIdKey="id"
        tableType="workspace-projects-table"
        columns={columns}
        onRowClick={(projectId) =>
          navigateToProfilePage({
            subjectType: "PROJECT",
            subjectId: projectId,
          })
        }
        errorMessage={ERROR_MESSAGE}
        shouldShowErrorMessage={shouldShowErrorMessage}
        onExport={() => CSVDataUtils.downloadProjectData(projects)}
      />
    </ContentLayout>
  );
}
